/**
 * /dev/story: the story over a scoped map, on a fake journey (a webshop order through ERP, the warehouse,
 * a split to the data warehouse, a delivery that failed with a 409 and was retried, an invoice, and an
 * e-invoice that failed with a 401). Uses src/map/Map.tsx when it exists; until then a plain static map
 * with the same MapGeometry, so the story can be judged on its own.
 */
import { Suspense, lazy, useLayoutEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import dayjs from 'dayjs';
import type { Message } from '../lib/api.ts';
import { systemColor, systemName } from '../lib/systems.ts';
import { Kicker, SystemBadge, cx } from '../design/primitives.tsx';
import type { MapFlow, MapGeometry, MapNode, MapProps, RoutedFlow } from '../map/types.ts';
import { Story } from '../story/Story.tsx';
import type { DocRef, Journey, PlayDoc } from '../story/engine.ts';

// ---------------------------------------------------------------- the fake data

const LANES = ['WEB', 'ERP', 'WMS', 'CAR', 'DWH', 'FIN'];
const mon = dayjs().day(1).hour(9).minute(12).second(7).millisecond(0);
const at = (days: number, h: number, m: number, s: number) => mon.add(days, 'day').hour(h).minute(m).second(s).valueOf();

const NODES: (MapNode & { col: number })[] = [
  { id: 'WebshopOrder', label: 'webshop orders', system: 'WEB', format: 'json', count: 1, countLabel: 'record', last: at(0, 9, 12, 7), usual: 3_600_000, col: 0, detail: '1 record · 2 d' },
  { id: 'ErpSalesOrder', label: 'sales orders', system: 'ERP', format: 'xml', count: 1, countLabel: 'record', last: at(1, 8, 3, 14), usual: 3_600_000, col: 1, detail: '1 record · 4 documents' },
  { id: 'WmsOutboundOrder', label: 'outbound orders', system: 'WMS', format: 'xml', count: 1, countLabel: 'record', last: at(0, 9, 40, 12), usual: 3_600_000, col: 2, detail: '1 record · 2 d' },
  { id: 'DwhSalesLine', label: 'sales lines', system: 'DWH', format: 'json', count: 3, countLabel: 'records', last: at(0, 9, 58, 3), usual: 3_600_000, col: 2, detail: '3 records · 2 d' },
  { id: 'WmsDelivery', label: 'deliveries', system: 'WMS', format: 'xml', count: 1, countLabel: 'record', last: at(1, 8, 3, 14), usual: 3_600_000, col: 3, failed: 1, detail: '1 record · 1 retried' },
  { id: 'CarTrackingEvent', label: 'tracking events', system: 'CAR', format: 'json', count: 2, countLabel: 'records', last: at(1, 9, 31, 0), usual: 3_600_000, col: 3, detail: '2 records · 1 d' },
  { id: 'ErpInvoice', label: 'invoices', system: 'ERP', format: 'xml', count: 1, countLabel: 'record', last: at(2, 11, 30, 40), usual: 3_600_000, col: 4, detail: '1 record · exported' },
  { id: 'FinEInvoice', label: 'e-invoices', system: 'FIN', format: 'json', count: 0, countLabel: 'records', last: null, usual: null, col: 5, absent: true, failed: 1, detail: 'not delivered' },
];

const FLOWS: MapFlow[] = [
  { id: '[WEB] Orders to ERP→ErpSalesOrder', process: '[WEB] Orders to ERP', system: 'WEB', from: 'WebshopOrder', to: 'ErpSalesOrder', runs: 1, failed: 0, label: '⚡ 1 run', formatIn: 'json', formatOut: 'xml' },
  { id: '[ERP] Sales Orders to WMS→WmsOutboundOrder', process: '[ERP] Sales Orders to WMS', system: 'ERP', from: 'ErpSalesOrder', to: 'WmsOutboundOrder', runs: 1, failed: 0, label: '⚡ 1 run · 28 min later', formatIn: 'xml', formatOut: 'xml' },
  { id: '[ERP] Sales Lines to DWH→DwhSalesLine', process: '[ERP] Sales Lines to DWH', system: 'ERP', from: 'ErpSalesOrder', to: 'DwhSalesLine', runs: 1, failed: 0, label: '⚡ 1 run · 1 → 3 lines', split: 3, formatIn: 'xml', formatOut: 'json' },
  { id: '[WMS] Delivery Confirmations to ERP→ErpSalesOrder', process: '[WMS] Delivery Confirmations to ERP', system: 'WMS', from: 'WmsDelivery', to: 'ErpSalesOrder', runs: 2, failed: 1, label: '⚡ 2 runs · 1 failed', formatIn: 'xml', formatOut: 'xml' },
  { id: '[WMS] Shipments to Carrier→CarTrackingEvent', process: '[WMS] Shipments to Carrier', system: 'WMS', from: 'WmsOutboundOrder', to: 'CarTrackingEvent', runs: 1, failed: 0, label: '⚡ 1 run', formatIn: 'xml', formatOut: 'json' },
  { id: '[ERP] Invoicing→ErpInvoice', process: '[ERP] Invoicing', system: 'ERP', from: 'ErpSalesOrder', to: 'ErpInvoice', runs: 1, failed: 0, label: '⏱ 11:30 · 1 invoice', formatIn: 'xml', formatOut: 'xml' },
  { id: '[FIN] E-invoices to Maventa→FinEInvoice', process: '[FIN] E-invoices to Maventa', system: 'FIN', from: 'ErpInvoice', to: 'FinEInvoice', runs: 1, failed: 1, label: '⚡ 1 run · failed · Maventa 401', formatIn: 'xml', formatOut: 'json' },
];

const NODE = 'demo';
const doc = (uuid: string, cls: string, type: string, when: number, run: string, process: string, status: string, info: string, fields: [string, string][]): PlayDoc & { fields: [string, string][] } => ({
  cls, type, at: when, executionId: run, process, status, info, ref: { vaultId: NODE, uuid, day: dayjs(when).format('YYYY-MM-DD') }, fields,
});
const DOCS = [
  doc('d1', 'WebshopOrder', 'json', at(0, 9, 12, 7), 'r1', '[WEB] Orders to ERP', 'SUCCESS', 'Fetched order WEB-857613 from the webshop',
    [['orderId', 'WEB-857613'], ['customer', 'Nastolan Lattiamestarit Oy'], ['total', '1379.50 EUR'], ['lines', '3']]),
  doc('d2', 'ErpSalesOrder', 'xml', at(0, 9, 12, 9), 'r1', '[WEB] Orders to ERP', 'SUCCESS', 'Order WEB-857613 created in ERP as SO-757613',
    [['SalesOrderNumber', 'SO-757613'], ['CustomersOrderReference', 'WEB-857613'], ['DeliveryAddress/City', 'Nastola'], ['SalesStatus', 'Backorder']]),
  doc('d3', 'WmsOutboundOrder', 'xml', at(0, 9, 40, 12), 'r2', '[ERP] Sales Orders to WMS', 'SUCCESS', 'Outbound order SO-757613 written to WMS as OUT_SO-757613.xml',
    [['OrderNumber', 'SO-757613'], ['Warehouse', 'VANTAA01'], ['Carrier', 'POSTI'], ['Rows', '3']]),
  doc('d4', 'DwhSalesLine', 'json', at(0, 9, 58, 1), 'r3', '[ERP] Sales Lines to DWH', 'SUCCESS', 'Line 1 of SO-757613 loaded to SALES_LINE — then line 2, then line 3',
    [['LINE_ID', 'SO-757613-1'], ['ITEM', '880101'], ['QTY', '4'], ['NET_AMOUNT', '519.60']]),
  doc('d5', 'DwhSalesLine', 'json', at(0, 9, 58, 2), 'r3', '[ERP] Sales Lines to DWH', 'SUCCESS', 'Line 2 of SO-757613 loaded to SALES_LINE',
    [['LINE_ID', 'SO-757613-2'], ['ITEM', '880115'], ['QTY', '2'], ['NET_AMOUNT', '178.00']]),
  doc('d6', 'DwhSalesLine', 'json', at(0, 9, 58, 3), 'r3', '[ERP] Sales Lines to DWH', 'SUCCESS', 'Line 3 of SO-757613 loaded to SALES_LINE',
    [['LINE_ID', 'SO-757613-3'], ['ITEM', '880122'], ['QTY', '16'], ['NET_AMOUNT', '401.60']]),
  doc('d7', 'WmsDelivery', 'xml', at(1, 7, 48, 10), 'r4', '[WMS] Delivery Confirmations to ERP', 'FAILED', 'Delivery confirmation SHP-757613 for SO-757613 failed: ERP OData 409 Conflict: sales order is locked by another user',
    [['ShipmentId', 'SHP-757613'], ['TrackingCode', 'JJFI647576134821'], ['Carrier', 'POSTI'], ['Rows', '3']]),
  doc('d8', 'WmsDelivery', 'xml', at(1, 8, 3, 14), 'r5', '[WMS] Delivery Confirmations to ERP', 'SUCCESS', 'Packing slip PS-757613 posted for SO-757613',
    [['ShipmentId', 'SHP-757613'], ['PackingSlipId', 'PS-757613'], ['TrackingCode', 'JJFI647576134821']]),
  doc('d9', 'ErpInvoice', 'xml', at(2, 11, 30, 40), 'r6', '[ERP] Invoicing', 'SUCCESS', 'Invoice 957613 created for SO-757613 and exported to Maventa',
    [['InvoiceNumber', '957613'], ['SalesOrderNumber', 'SO-757613'], ['InvoiceTotalVatIncludedAmount', '1379.50'], ['PaymentTermsFreeText', 'Net 14']]),
  doc('d10', 'FinEInvoice', 'json', at(2, 11, 31, 2), 'r7', '[FIN] E-invoices to Maventa', 'FAILED', 'Invoice 957613 for SO-757613 failed: Maventa 401 Unauthorized (token expired)',
    [['invoiceNumber', '957613'], ['recipient', 'Nastolan Lattiamestarit Oy'], ['amount', '1379.50'], ['status', 'FAILED']]),
];
const JOURNEY: Journey = { title: 'The story of sales order SO-757613', kind: 'record', docs: DOCS };

/** The panel's fields without a cloud: the fake documents answer as Api.message would. */
const loadDoc = async (r: DocRef): Promise<Message> => {
  const d = DOCS.find((x) => x.ref.uuid === r.uuid);
  if (!d) throw new Error('No such document');
  return { uuid: d.ref.uuid, messageClass: d.cls, type: d.type!, timestamp: d.at, meta: { status: d.status!, process: d.process! }, flags: [], day: d.ref.day!, fields: d.fields };
};

// ---------------------------------------------------------------- the real map, when it exists

const mapModules = import.meta.glob('../map/Map.tsx');
const mapLoader = mapModules['../map/Map.tsx'];
const RealMap = mapLoader
  ? lazy(async () => {
    const m = (await mapLoader()) as Record<string, unknown>;
    return { default: (m.Map ?? m.default) as ComponentType<MapProps> };
  })
  : null;

// ---------------------------------------------------------------- a plain map with the same geometry

const LANE_H = 96;
const NODE_H = 72;
const NODE_W = 164;
const LABEL_W = 148;
const TOP = 12;
type Pt = { x: number; y: number };

/** An orthogonal route with rounded corners, and where its points are along the way. */
function route(points: Pt[]) {
  const segs = points.slice(1).map((p, i) => ({ a: points[i], b: p, l: Math.hypot(p.x - points[i].x, p.y - points[i].y) }));
  const length = segs.reduce((n, s) => n + s.l, 0);
  const R = 10;
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i - 1];
    const c = points[i];
    const n = points[i + 1];
    const r = Math.min(R, Math.hypot(c.x - p.x, c.y - p.y) / 2, Math.hypot(n.x - c.x, n.y - c.y) / 2);
    const u1 = { x: Math.sign(c.x - p.x), y: Math.sign(c.y - p.y) };
    const u2 = { x: Math.sign(n.x - c.x), y: Math.sign(n.y - c.y) };
    d += ` L${c.x - u1.x * r},${c.y - u1.y * r} Q${c.x},${c.y} ${c.x + u2.x * r},${c.y + u2.y * r}`;
  }
  d += ` L${points[points.length - 1].x},${points[points.length - 1].y}`;
  const pointAt = (t: number) => {
    let left = Math.max(0, Math.min(1, t)) * length;
    for (const s of segs) {
      if (left <= s.l || s === segs[segs.length - 1]) {
        const k = s.l ? Math.min(1, left / s.l) : 0;
        return { x: s.a.x + (s.b.x - s.a.x) * k, y: s.a.y + (s.b.y - s.a.y) * k, angle: Math.atan2(s.b.y - s.a.y, s.b.x - s.a.x) };
      }
      left -= s.l;
    }
    const e = points[points.length - 1];
    return { x: e.x, y: e.y, angle: 0 };
  };
  const mid = pointAt(0.5);
  return { d, length, pointAt, mid: { x: mid.x, y: mid.y } };
}

function FallbackMap({ geometryRef, dimmed }: { geometryRef: (g: MapGeometry | null) => void; dimmed: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1200);
  useLayoutEffect(() => {
    if (!box.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const laid = useMemo(() => {
    const cols = Math.max(...NODES.map((n) => n.col)) + 1;
    const colW = Math.max(190, (width - LABEL_W) / cols);
    const placed = NODES.map((n) => ({
      ...n, lane: LANES.indexOf(n.system), x: LABEL_W + n.col * colW + (colW - NODE_W) / 2, y: TOP + LANES.indexOf(n.system) * LANE_H + (LANE_H - NODE_H) / 2, w: NODE_W, h: NODE_H,
    }));
    const byId = new Map(placed.map((n) => [n.id, n]));
    const routes = new Map<string, ReturnType<typeof route>>();
    const routed: RoutedFlow[] = FLOWS.map((f) => {
      const a = byId.get(f.from)!;
      const b = byId.get(f.to!)!;
      let pts: Pt[];
      if (b.col > a.col) {
        const midX = a.x + a.w + (b.x - a.x - a.w) / 2;
        pts = a.y === b.y ? [{ x: a.x + a.w, y: a.y + a.h / 2 }, { x: b.x, y: b.y + b.h / 2 }]
          : [{ x: a.x + a.w, y: a.y + a.h / 2 }, { x: midX, y: a.y + a.h / 2 }, { x: midX, y: b.y + b.h / 2 }, { x: b.x, y: b.y + b.h / 2 }];
      } else {
        // Backwards: out of the top, along the gap between the lanes, down into the target
        const gapY = TOP + Math.max(a.lane, b.lane) * LANE_H;
        pts = [{ x: a.x + a.w / 2, y: a.y }, { x: a.x + a.w / 2, y: gapY }, { x: b.x + b.w / 2 + 28, y: gapY }, { x: b.x + b.w / 2 + 28, y: b.y + b.h }];
      }
      const r = route(pts);
      routes.set(f.id, r);
      return { ...f, d: r.d, length: r.length, labelAt: { x: pts[pts.length - 1].x - 8, y: pts[pts.length - 1].y - 14 }, swapAt: 0.5 };
    });
    const geometry: MapGeometry = {
      box: (id) => {
        const n = byId.get(id);
        return n ? { x: n.x, y: n.y, w: n.w, h: n.h } : null;
      },
      path: (flowId) => routes.get(flowId) ?? null,
      flowBetween: (from, to, process) => routed.find((f) => f.from === from && f.to === to && (!process || f.process === process)) ?? routed.find((f) => f.from === from && f.to === to) ?? null,
    };
    return { placed, routed, geometry, width: Math.max(width, LABEL_W + cols * colW), height: TOP * 2 + LANES.length * LANE_H };
  }, [width]);
  useLayoutEffect(() => {
    geometryRef(laid.geometry);
    return () => geometryRef(null);
  }, [laid, geometryRef]);

  return (
    <div ref={box} className="relative" style={{ height: laid.height }}>
      <div className="relative" style={{ width: laid.width, height: laid.height }}>
        {LANES.map((s, i) => (
          <div key={s} className="absolute left-0 right-0 flex items-center gap-2.5 pl-4 rounded-card" style={{ top: TOP + i * LANE_H, height: LANE_H - 4, background: `color-mix(in srgb, ${systemColor(s)} var(--tint), transparent)` }}>
            <SystemBadge code={s} size={24} />
            <span className="text-small text-n8">{systemName(s)}</span>
          </div>
        ))}
        <svg className="absolute inset-0 overflow-visible" width={laid.width} height={laid.height} aria-hidden="true">
          <defs>
            <marker id="demo-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M1,1 L9,5 L1,9" fill="none" stroke="var(--n6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></marker>
            <marker id="demo-arrow-fail" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M1,1 L9,5 L1,9" fill="none" stroke="var(--fail)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></marker>
          </defs>
          {laid.routed.map((f) => (
            <g key={f.id} style={{ opacity: dimmed ? 0.45 : 1, transition: 'opacity var(--t-layout) var(--ease)' }}>
              <path d={f.d} fill="none" stroke={f.failed ? 'var(--fail)' : 'var(--n5)'} strokeWidth={1.5} strokeLinecap="round" markerEnd={`url(#demo-arrow${f.failed ? '-fail' : ''})`} />
              <text x={f.labelAt.x} y={f.labelAt.y} textAnchor="end" fontFamily="var(--mono)" fontSize={10.5} fill={f.failed ? 'var(--fail)' : 'var(--n7)'} stroke="var(--n1)" strokeWidth={3} paintOrder="stroke" strokeLinejoin="round">{f.label}</text>
            </g>
          ))}
        </svg>
        {laid.placed.map((n) => (
          <div key={n.id} className={cx('absolute flex flex-col justify-center gap-0.5 px-3.5 rounded-card', n.absent ? 'border border-dashed border-n6' : 'bg-n2 shadow-node')}
            style={{ left: n.x, top: n.y, width: n.w, height: n.h }}>
            <span className={cx('text-small font-medium leading-tight', n.absent ? 'text-n8' : 'text-n10')}>{n.label}</span>
            <span className={cx('font-mono text-micro truncate', n.failed ? 'text-fail' : 'text-n7')}>{n.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the demo page

export default function StoryDemo() {
  const [open, setOpen] = useState(true);
  const [speed, setSpeed] = useState(1);
  // One "now" for the map: without it the map re-lays out on every render, and every layout re-renders
  // this page through geometryRef
  const now = useMemo(() => Date.now(), []);
  const [geometry, setGeometry] = useState<MapGeometry | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  return (
    <div className="max-w-[1440px] mx-auto px-6 pt-6 pb-8 flex flex-col gap-5">
      <div className="stage-dim flex flex-col gap-3">
        <Kicker>record · sales order · WEB → ERP → WMS → DWH → FIN · last seen 3 min ago</Kicker>
        <p className="m-0 text-title font-medium leading-[1.32] tracking-[-.012em] max-w-[1000px]">
          SO-757613 is a webshop order of Nastolan Lattiamestarit Oy, placed on Monday. It reached ERP 09:12, the warehouse 09:40 and the data
          warehouse 09:58 as 3 lines; the delivery was confirmed Tuesday 08:03 on the second try. The invoice was exported today 11:31 but never reached Maventa.
        </p>
        {!open && (
          <button type="button" onClick={() => setOpen(true)}
            className="self-start inline-flex items-center gap-2 h-9 px-4 rounded-full bg-n2 text-n10 text-small font-medium border-0 shadow-node cursor-pointer hover:bg-n3">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 2v10l8.5-5z" fill="currentColor" /></svg>
            Tell the story
          </button>
        )}
      </div>
      {/* The map's own box carries .stage-map and the ref: no padding of its own, so its top-left is MapGeometry's origin */}
      <div className="rounded-stage bg-n2 p-4">
        <div ref={mapRef} className="stage-map">
          {RealMap ? (
            <Suspense fallback={<FallbackMap geometryRef={setGeometry} dimmed={open} />}>
              <RealMap nodes={NODES} flows={FLOWS} now={now} motion={open ? 'paused' : 'live'} speed={speed} glyphScale={0.8} geometryRef={setGeometry} />
            </Suspense>
          ) : <FallbackMap geometryRef={setGeometry} dimmed={open} />}
        </div>
      </div>
      {open && (
        <Story journey={JOURNEY} flows={FLOWS} nodes={NODES} geometry={geometry} mapRef={mapRef} speed={speed} onSpeed={setSpeed}
          loadDoc={loadDoc} onMessage={(r) => console.info('open document', r)} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}
